import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon,
  FileCode2,
  AlertCircle,
  Download,
  Copy,
  Check,
  Printer,
} from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/data-matrix-generator";
const TITLE = "Data Matrix Generator — Create 2D Codes for UDI & Parts";
const DESC =
  "Free Data Matrix generator. Encode text, UDI or GS1 payloads as a Data Matrix barcode and download as PNG or SVG — fully client-side, no uploads.";

const FAQ = [
  {
    q: "What is a Data Matrix code?",
    a: "Data Matrix is a 2D barcode made of black-and-white modules arranged in a square or rectangle. It packs a lot of data into a tiny footprint, which is why it dominates healthcare UDI, electronics, and direct-part marking on metal.",
  },
  {
    q: "When should I use Data Matrix instead of QR?",
    a: "Pick Data Matrix when you need to mark very small items, etch on metal, or follow industry standards that mandate it (medical devices under FDA UDI / EU MDR, pharma serialisation, aerospace parts). Pick QR for consumer-facing scans where phones are the reader.",
  },
  {
    q: "What is GS1 Data Matrix mode?",
    a: "GS1 Data Matrix uses FNC1 as the first character to tell scanners the payload is structured GS1 Application Identifiers (AIs). Enable the GS1 toggle and start your payload with parenthesised AIs, e.g. (01)09506000134352(17)261231(10)ABC123.",
  },
  {
    q: "What Size Should I Choose?",
    a: "Auto picks the smallest symbol that fits your data. Force a specific size only if your label artwork demands a fixed footprint. Square sizes range from 10×10 to 144×144; rectangles from 8×18 to 16×48.",
  },
  {
    q: "Is My Data Uploaded Anywhere?",
    a: "No. The barcode is rendered locally with the open-source bwip-js library. Nothing leaves your browser.",
  },
  {
    q: "Is the Data Matrix Generator Free?",
    a: "Yes. This Data Matrix creator costs nothing, needs no account and adds no watermark, and there is no cap on how many codes you make. The PNG, SVG and print downloads are free as well, because your browser renders them.",
  },
  {
    q: "How Do I Generate a Data Matrix Code in Excel?",
    a: "Excel has no built-in Data Matrix function. For a few codes, paste each cell's value into this Data Matrix barcode generator, download the PNG or SVG and place it in the sheet with Insert > Pictures. For hundreds of rows you need a barcode add-in, or a Data Matrix font together with its encoder, because a font alone cannot calculate the error correction codewords.",
  },
  {
    q: "What Is the Difference Between a Data Matrix Barcode and a QR Code?",
    a: "A Data Matrix is found by its L-shaped border on two sides, while a QR code is found by three squares in its corners. Data Matrix error correction is fixed for each symbol size and the symbol can be rectangular; a QR code is always square and lets you choose one of four error correction levels. Data Matrix suits small parts and GS1 healthcare labels, and QR suits codes that the public scans with a phone.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Data Matrix Generator", path: PATH },
];

const SQUARE_SIZES = [
  "10x10",
  "12x12",
  "14x14",
  "16x16",
  "18x18",
  "20x20",
  "22x22",
  "24x24",
  "26x26",
  "32x32",
  "36x36",
  "40x40",
  "44x44",
  "48x48",
  "52x52",
  "64x64",
  "72x72",
  "80x80",
  "88x88",
  "96x96",
  "104x104",
  "120x120",
  "132x132",
  "144x144",
];
const RECT_SIZES = ["8x18", "8x32", "12x26", "12x36", "16x36", "16x48"];

type EncodingMode = "auto" | "ascii" | "c40" | "text" | "base256";

function validate(data: string, gs1: boolean): string | null {
  if (data.length === 0) return "Enter the data to encode.";
  if (data.length > 2335)
    return `Data is ${data.length} chars — Data Matrix max is 2335 alphanumeric chars.`;
  if (gs1) {
    const trimmed = data.trimStart();
    if (!trimmed.startsWith("(") && !trimmed.startsWith("\u001d")) {
      return "GS1 mode requires the payload to start with (AI) syntax, e.g. (01)09506000134352.";
    }
  }
  return null;
}

function safeFilename(data: string): string {
  const cleaned = data
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `datamatrix-${cleaned || "code"}`;
}

function DataMatrixGeneratorPage() {
  const [data, setData] = useState("(01)09506000134352(17)261231(10)ABC123");
  const [printOpen, setPrintOpen] = useState(false);
  const [encoding, setEncoding] = useState<EncodingMode>("auto");
  const [size, setSize] = useState<string>("auto");
  const [moduleSize, setModuleSize] = useState(4);
  const [quietZone, setQuietZone] = useState(2);
  const [gs1, setGs1] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const validationErr = useMemo(() => validate(data, gs1), [data, gs1]);

  useEffect(() => {
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      try {
        const opts = {
          bcid: gs1 ? "gs1datamatrix" : "datamatrix",
          text: data,
          scale: Math.max(1, Math.min(10, moduleSize)),
          padding: Math.max(0, Math.min(40, quietZone * 4)),
          backgroundcolor: "FFFFFF",
        } as Record<string, unknown> & { bcid: string; text: string };
        if (gs1) opts.parsefnc = true;
        if (encoding !== "auto") opts.encoding = encoding;
        if (size !== "auto") {
          const [r, c] = size.split("x").map(Number);
          opts.rows = r;
          opts.columns = c;
        }
        if (canvasRef.current) bwipjs.toCanvas(canvasRef.current, opts);
        const svg = bwipjs.toSVG(opts);
        setSvgMarkup(svg);
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to render code.";
        setError(msg);
        setSvgMarkup("");
        const c = canvasRef.current;
        if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [data, encoding, size, moduleSize, quietZone, gs1, validationErr]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${safeFilename(data)}.png`;
      a.click();
      URL.revokeObjectURL(u);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${safeFilename(data)}.svg`;
    a.click();
    URL.revokeObjectURL(u);
  };

  const copyData = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Data Matrix Generator"
      description="Encode text, UDI or GS1 payloads as a Data Matrix barcode. Download as PNG or SVG — fully client-side. Choose Auto or one of 30 square and rectangular symbol sizes, switch on GS1 mode for Application Identifiers, and set the encoding, module size and quiet zone."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the Data Matrix generator"
            about={
              <>
                <p>
                  The ReadBarcode Data Matrix generator encodes text, UDI or GS1 payloads as a Data
                  Matrix 2D barcode and renders both PNG and SVG output — all locally with the
                  open-source bwip-js library.
                </p>
                <p>
                  Data Matrix is the dominant 2D format outside consumer QR: medical device UDI under
                  FDA / EU MDR, pharma serialisation (PPN / GS1 DataMatrix), electronics, aerospace
                  direct-part marking, and the GS1 Sunrise 2027 retail rollout all rely on it. Toggle
                  GS1 mode to insert the required FNC1 prefix so scanners parse the payload as
                  structured Application Identifiers.
                </p>
              </>
            }
            whenToUse={[
              "Marking medical devices for FDA UDI or EU MDR.",
              "Pharma serialisation (GS1 DataMatrix with GTIN + lot + expiry + serial).",
              "Direct-part marking on metal, PCBs, or small electronics.",
              "Aerospace, automotive, and defence part traceability.",
              "Fresh-food variable-weight labelling alongside GS1 Digital Link.",
              "Embedding URLs or short payloads where Data Matrix is mandated.",
            ]}
            howItWorks={[
              { title: "Enter your data", desc: "Type or paste up to 2335 alphanumeric characters." },
              { title: "Choose GS1 mode", desc: "Enable for structured GS1 payloads with FNC1." },
              {
                title: "Pick a size",
                desc: "Auto fits your data; force a size only when label artwork requires it.",
              },
              {
                title: "Tune scale & quiet zone",
                desc: "Bigger module size scans further; bigger quiet zone helps imperfect printers.",
              },
              {
                title: "Download PNG or SVG",
                desc: "SVG for print and labels; PNG for screen proofs.",
              },
            ]}
            whatToAvoid={[
              "Putting parenthesised AIs in non-GS1 mode — scanners won't strip the brackets.",
              "Forcing a tiny symbol size that can't hold your data — switch back to Auto.",
              "Setting module size too small for the target printer's DPI — the modules blur together.",
              "Skipping the quiet zone — most scanners need at least 1 module of clearance.",
              "Encoding sensitive data in plain text — anyone with a phone can decode it.",
            ]}
            tips={[
              "Use rectangular sizes (e.g. 16x48) for narrow labels like cable flags.",
              "For metal direct-part marking, scale modules up; dot-peen scanners need larger cells.",
              "Round-trip-test your output with the Data Matrix Reader on this site.",
              "GS1 DataMatrix payloads should match the receiver's master data exactly.",
              "Keep payloads short — every extra character adds modules and shrinks scan margin.",
            ]}
          />
          <PrivacyNote />
          <DataMatrixSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="data">Data to encode</Label>
              <Textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                rows={5}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-sm"
                placeholder="(01)09506000134352(17)261231(10)ABC123"
              />
              <p className="text-xs text-muted-foreground">
                Up to 2335 alphanumeric characters. For GS1, use parenthesised AIs.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="gs1" className="cursor-pointer">
                  GS1 Data Matrix mode
                </Label>
                <p className="text-xs text-muted-foreground">Adds FNC1 prefix and parses AIs.</p>
              </div>
              <Switch id="gs1" checked={gs1} onCheckedChange={setGs1} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="encoding">Encoding</Label>
                <Select value={encoding} onValueChange={(v) => setEncoding(v as EncodingMode)}>
                  <SelectTrigger id="encoding">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="ascii">ASCII</SelectItem>
                    <SelectItem value="c40">C40</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="base256">Base 256</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="size">Symbol size</Label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger id="size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="auto">Auto</SelectItem>
                    {SQUARE_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s} (square)
                      </SelectItem>
                    ))}
                    {RECT_SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s} (rect)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="moduleSize">Module size ({moduleSize})</Label>
                <Input
                  id="moduleSize"
                  type="range"
                  min={1}
                  max={10}
                  value={moduleSize}
                  onChange={(e) => setModuleSize(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quietZone">Quiet zone ({quietZone})</Label>
                <Input
                  id="quietZone"
                  type="range"
                  min={0}
                  max={4}
                  value={quietZone}
                  onChange={(e) => setQuietZone(Number(e.target.value))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated Data Matrix preview"
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">Encoded payload</div>
                  <code className="mt-1 block break-all text-xs">{data || "—"}</code>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={copyData} disabled={!data}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`Data Matrix · ${data.slice(0, 60) || "code"}`}
        filename={safeFilename(data)}
        svgMarkup={svgMarkup}
        defaultCaption={data}
        defaults={{
          layoutKind: "single",
          widthMm: 25,
          sheetPreset: "averyL7160",
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        Data Matrix codes are rendered locally with bwip-js. The payload you enter never touches
        our servers.
      </p>
    </>
  );
}

function DataMatrixSections() {
  return (
    <>
      <h2>GS1 Data Matrix Generator for UDI and Pharma</h2>
      <p>With GS1 mode on, this is a GS1 Data Matrix generator: the symbol starts with FNC1 and your bracketed Application Identifiers become GS1 element strings, the form used on GS1 healthcare labels.</p>
      <h3>UDI Data Matrix Codes</h3>
      <p>A UDI pairs a device identifier with production identifiers. With GS1 as the issuing agency, the device identifier is the GTIN in AI (01), followed by AIs such as (17) expiry, (10) lot and (21) serial number. Decode the result with the <Link to="/data-matrix-reader">Data Matrix reader</Link> to confirm each element.</p>
      <h2>2D Data Matrix Generator vs QR Code</h2>
      <p>Data Matrix usually fits the same data into a smaller symbol with a narrower margin, while QR is the code consumers scan with phones.</p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Data Matrix (ECC 200)</th>
              <th scope="col">QR Code</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Finder pattern</td>
              <td>L-shaped border on two sides</td>
              <td>Three corner squares</td>
            </tr>
            <tr>
              <td>Quiet zone</td>
              <td>1 module</td>
              <td>4 modules</td>
            </tr>
            <tr>
              <td>Error correction</td>
              <td>Fixed by symbol size</td>
              <td>Levels L, M, Q or H</td>
            </tr>
          </tbody>
        </table>
      </TableScroll>
      <p>When space is tight, a 2D matrix generator that outputs Data Matrix is the better pick; the <Link to="/barcode-guides/1d-vs-2d-barcodes">1D vs 2D barcodes guide</Link> covers where either replaces a linear barcode.</p>
      <h2>Data Matrix Code Generator Settings: Size and ECC 200</h2>
      <p>This online Data Matrix generator is an ECC200 Data Matrix generator: every code uses ECC 200, the Reed-Solomon version GS1 requires. Error correction comes with the symbol size, so there is no level to pick.</p>
      <ul>
        <li>Symbol size: Auto, or one of 24 square and 6 rectangular sizes.</li>
        <li>Encoding: Auto, ASCII, C40, Text or Base 256.</li>
        <li>Module size and quiet zone: keep the margin at least one module wide.</li>
      </ul>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/data-matrix-reader",
      label: "Data Matrix Reader",
      desc: "Decode the Data Matrix you just generated.",
    },
    {
      path: "/gs1-digital-link-generator",
      label: "GS1 Digital Link Generator",
      desc: "Build a Sunrise-2027 URL as QR or Data Matrix.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D Barcodes",
      desc: "Where 2D codes such as Data Matrix replace linear barcodes.",
    },
    {
      path: "/barcode-generator",
      label: "Barcode Generator",
      desc: "1D barcodes: UPC, EAN, Code 128 and more.",
    },
    {
      path: "/gs1-digital-link-decoder",
      label: "GS1 Digital Link Decoder",
      desc: "Parse a GS1 URL into structured fields.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/data-matrix-generator")({
  component: DataMatrixGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Data Matrix Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
